import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Ban, Phone, MessageCircle } from "lucide-react";

const statusMap = (isEN) => ({
  disponibile:  { cls: "bg-emerald-500/15 text-emerald-400", label: isEN ? "Available" : "Disponibile" },
  prenotato:    { cls: "bg-amber-500/15 text-amber-300",     label: isEN ? "Booked · check future dates" : "Prenotato · verifica date future" },
  noleggiato:   { cls: "bg-amber-500/15 text-amber-300",     label: isEN ? "Rented · check future dates" : "Noleggiato · verifica date future" },
  sospeso:      { cls: "bg-sky-500/15 text-sky-300",         label: isEN ? "Off-season · next season only" : "Rimessaggio · prenotabile prossima stagione" },
  manutenzione: { cls: "bg-zinc-500/20 text-zinc-300",       label: isEN ? "In maintenance" : "In manutenzione" },
});

/**
 * A single vehicle card used in /catalog grids.
 * Visual variants: available (clickable Link) vs unavailable (plain div, dimmed + CTA).
 *
 * Props:
 *  - s            (scooter row from /api/scooters)
 *  - isUnavail    (boolean)        — true → render as <div> with greyscale overlay + WhatsApp/phone CTA
 *  - linkQuery    (string)         — query string appended to /scooter/:id, e.g. "?start_at=…&end_at=…"
 *  - isEN, t      (i18n helpers)
 */
export default function ScooterCard({ s, isUnavail, linkQuery = "", inDateFlow = false, isEN, t }) {
  const isMaintenance = s.status === "manutenzione";
  // Iter 54 — winter-storage vehicles: still clickable (customer can pick a
  // date in the next season and complete a booking), but the badge tells
  // them the vehicle is out until May 15 of the next year.
  const isWinterStorage = s.status === "sospeso";
  const nextSeasonYear = s.next_season_start ? s.next_season_start.slice(0, 4) : null;
  const nextSeasonDate = s.next_season_start
    ? `${s.next_season_start.slice(8, 10)}/${s.next_season_start.slice(5, 7)}/${s.next_season_start.slice(0, 4)}`
    : null;
  // Iter 41 — when the customer has picked a date range (Catalog.jsx passes
  // inDateFlow=true), the badge must reflect availability for THOSE dates,
  // not today's live status. A scooter currently rented today could be free
  // in a month — showing "Noleggiato" confused customers into thinking it
  // was unavailable for their selection.
  const dateFlow = inDateFlow || !!linkQuery;
  let st;
  if (dateFlow) {
    if (isUnavail) {
      if (isMaintenance) {
        st = statusMap(isEN).manutenzione;
      } else if (isWinterStorage && nextSeasonYear) {
        st = {
          cls: "bg-sky-500/15 text-sky-300",
          label: isEN
            ? `Bookable for ${nextSeasonYear} season`
            : `Prenotabile per stagione ${nextSeasonYear}`,
        };
      } else {
        st = { cls: "bg-red-500/15 text-red-300", label: isEN ? "Not available on your dates" : "Non disponibile in queste date" };
      }
    } else {
      st = { cls: "bg-emerald-500/15 text-emerald-400", label: isEN ? "Available on your dates" : "Disponibile per le tue date" };
    }
  } else {
    st = statusMap(isEN)[s.status] || statusMap(isEN).disponibile;
  }
  // Iter 54 — winter-storage cards stay CLICKABLE (customer can jump to the
  // detail page to book for the next season). Only maintenance/booked cards
  // are dimmed + non-interactive.
  const isClickableWinter = isWinterStorage && !isMaintenance;
  const cardCls = `group border rounded-xl overflow-hidden transition-all ${
    isUnavail && !isClickableWinter
      ? "border-zinc-800 bg-zinc-900/20 opacity-70"
      : "border-zinc-800 hover:border-[#FFD700]/60 bg-zinc-900/40 hover:-translate-y-1"
  }`;
  // If winter storage → link the card to /scooter/:id with next-season dates
  // pre-filled so the date picker on detail already sits in the bookable window.
  const winterLinkQuery = (isClickableWinter && s.next_season_start)
    ? (() => {
        const start = s.next_season_start;
        // default a 3-day rental starting on 15/05 next year
        const endDate = new Date(start);
        endDate.setDate(endDate.getDate() + 3);
        const end = endDate.toISOString().slice(0, 10);
        return `?start_at=${encodeURIComponent(start)}&end_at=${encodeURIComponent(end)}`;
      })()
    : linkQuery;

  const inner = (
    <>
      <div className="aspect-[16/11] overflow-hidden bg-zinc-900 relative">
        <img
          src={s.photo_url || "https://images.unsplash.com/photo-1647265063096-2b963404d534?w=800"}
          alt={s.model}
          className={`w-full h-full object-cover transition-transform duration-500 ${isUnavail && !isClickableWinter ? "grayscale" : "group-hover:scale-105"}`}
        />
        {isUnavail && !isClickableWinter && (
          <div className="absolute inset-0 bg-zinc-950/55 flex items-center justify-center">
            <span
              className="px-3 py-1 rounded-full bg-black/80 border border-red-400/40 text-[11px] font-semibold text-red-200 shadow-lg backdrop-blur-sm flex items-center gap-1.5"
              data-testid={`unavail-badge-${s.id}`}
            >
              <Ban className="w-3 h-3" />
              {isMaintenance
                ? (isEN ? "In maintenance" : "In manutenzione")
                : (isEN ? "Not available on these dates" : "Non disponibile in queste date")}
            </span>
          </div>
        )}
        {isClickableWinter && nextSeasonYear && (
          <div className="absolute top-2 right-2 px-2.5 py-1 rounded-md bg-sky-500 text-white text-xs font-bold shadow-lg flex items-center gap-1"
               data-testid={`winter-badge-${s.id}`}>
            <span>❄</span>
            <span>{isEN ? `Season ${nextSeasonYear}` : `Stagione ${nextSeasonYear}`}</span>
          </div>
        )}
        {s.last_minute_active && !isUnavail && (
          <div
            className="absolute top-2 left-2 px-2.5 py-1 rounded-md bg-red-500 text-white text-xs font-bold shadow-lg flex items-center gap-1"
            data-testid="last-minute-badge"
          >
            <span>🔥</span>
            <span>{isEN ? "LAST MINUTE" : "LAST MINUTE"}</span>
            {s.last_minute_discount_pct ? <span className="ml-0.5">−{Number(s.last_minute_discount_pct)}%</span> : null}
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-display font-bold text-xl tracking-tight">{s.model}</h3>
          <Badge
            className={`${st.cls} border-0 font-medium text-[10px] uppercase tracking-wider whitespace-nowrap`}
            data-testid={`scooter-status-${s.id}`}
          >
            {st.label}
          </Badge>
        </div>
        <p className="text-sm text-zinc-400 mt-1">{s.displacement} cc · {s.plate}</p>
        {s.deposit_amount > 0 && (
          <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug" data-testid={`scooter-deposit-${s.id}`}>
            <span className="text-[#FFD700]">{isEN ? "Deposit" : "Cauzione"}</span>: €{Number(s.deposit_amount).toFixed(0)}
          </p>
        )}
        <div className="mt-4 flex items-baseline justify-between">
          <div>
            <span className="font-display font-bold text-2xl text-[#FFD700]">€{(s.daily_rate || s.hourly_rate || 0).toFixed(2)}</span>
            <span className="text-zinc-500 text-sm"> {t("detail.per_day")}</span>
          </div>
          {!isUnavail && (
            <span className="text-xs text-zinc-500 group-hover:text-[#FFD700] transition-colors">
              {t("catalog.details")} →
            </span>
          )}
          {isClickableWinter && (
            <span className="text-xs text-sky-400 group-hover:text-sky-300 transition-colors">
              {isEN ? "Prebook →" : "Prenota →"}
            </span>
          )}
        </div>

        {isClickableWinter && nextSeasonDate && (
          <div
            className="mt-4 rounded-lg border border-sky-500/30 bg-sky-500/[0.05] p-3"
            data-testid={`winter-cta-${s.id}`}
          >
            <p className="text-xs text-zinc-300 leading-relaxed">
              {isEN
                ? <>Vehicle in winter storage · <span className="text-sky-300 font-semibold">bookable from {nextSeasonDate}</span> (season {nextSeasonYear}).</>
                : <>Veicolo in rimessaggio invernale · <span className="text-sky-300 font-semibold">prenotabile dal {nextSeasonDate}</span> (stagione {nextSeasonYear}).</>}
            </p>
          </div>
        )}

        {isUnavail && !isClickableWinter && (
          <div
            className="mt-4 rounded-lg border border-[#FFD700]/30 bg-[#FFD700]/[0.05] p-3"
            data-testid={`maint-cta-${s.id}`}
          >
            <p className="text-xs text-zinc-300 leading-relaxed mb-2">
              {isEN
                ? "Other dates? Call us or message us on WhatsApp — we'll check what's possible."
                : "Altre date? Chiamaci o scrivici su WhatsApp — verifichiamo cosa possiamo fare."}
            </p>
            <div className="flex gap-2">
              <a
                href="tel:+390565904134"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center justify-center gap-1.5 flex-1 px-3 py-1.5 rounded-md bg-zinc-900 border border-zinc-700 hover:border-[#FFD700] text-xs font-medium text-zinc-100 hover:text-[#FFD700] transition"
                data-testid={`maint-call-${s.id}`}
              >
                <Phone className="w-3.5 h-3.5" /> 0565 904134
              </a>
              <a
                href="https://wa.me/390565904134"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center justify-center gap-1.5 flex-1 px-3 py-1.5 rounded-md bg-emerald-600/20 border border-emerald-600/40 hover:bg-emerald-600/30 text-xs font-medium text-emerald-300 transition"
                data-testid={`maint-wa-${s.id}`}
              >
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </a>
            </div>
          </div>
        )}
      </div>
    </>
  );

  // Iter 54 — winter storage vehicles remain clickable (customer can prebook
  // for the next season). Non-winter unavailable cards stay non-interactive.
  if (isUnavail && !isClickableWinter) {
    return <div className={cardCls} data-testid="catalog-scooter-card">{inner}</div>;
  }
  return (
    <Link to={`/scooter/${s.id}${winterLinkQuery}`} className={cardCls} data-testid="catalog-scooter-card">
      {inner}
    </Link>
  );
}
